/**
 * Module Registry - Single Source of Truth
 *
 * This file defines all application modules and their metadata.
 * All other parts of the application should derive module information from here.
 *
 * Module ID format: lowercase with underscores (e.g., "report_builder")
 * Route path format: lowercase with hyphens (e.g., "/report-builder")
 */

import {
  faTableColumns,
  faFilters,
  faChartMixed,
  faFolder,
  faServer,
  faListCheck,
  faQuestionCircle,
  faSplit,
  faCirclePlus,
  faTable,
  faCalculator,
  faDice,
  faSliders,
  faChartLine,
  faFlask,
  faChartPie,
  faBriefcase,
  faWrench,
  faPlus,
  faGlobe,
  faFileLines,
  faFilePen,
  faUsers,
  faUserRobot,
  faBoxOpen,
  faBalanceScale,
  faClipboardCheck,
  faCalendarCheck,
  faDiagramSubtask,
  faBullseyeArrow,
  faAddressCard,
  faUserPlus,
  faFileSignature,
  faRocket,
  faFiles,
  faSparkles,
  faHouse,
  faGear,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import {
  faObjectsColumn as faObjectsColumnDuo,
  faFiles as faFilesDuo,
  faFilters as faFiltersDuo,
  faChartMixed as faChartMixedDuo,
  faFolder as faFolderDuo,
  faClockRotateLeft as faClockRotateLeftDuo,
  faServer as faServerDuo,
  faComments as faCommentsDuo,
  faRocket as faRocketDuo,
  faBriefcase as faBriefcaseDuo,
  faListCheck as faListCheckDuo,
  faQuestionCircle as faQuestionCircleDuo,
  faSplit as faSplitDuo,
  faCirclePlus as faCirclePlusDuo,
  faTable as faTableDuo,
  faWrench as faWrenchDuo,
  faPlus as faPlusDuo,
  faCalculator as faCalculatorDuo,
  faDiagramSubtask as faDiagramSubtaskDuo,
  faDice as faDiceDuo,
  faSliders as faSlidersDuo,
  faChartLine as faChartLineDuo,
  faFlask as faFlaskDuo,
  faChartPie as faChartPieDuo,
  faGlobe as faGlobeDuo,
  faPaintbrushPencil as faPaintbrushPencilDuo,
  faBalanceScale as faBalanceScaleDuo,
  faClipboardCheck as faClipboardCheckDuo,
  faCalendarCheck as faCalendarCheckDuo,
  faBullseyeArrow as faBullseyeArrowDuo,
  faAddressCard as faAddressCardDuo,
  faUserPlus as faUserPlusDuo,
  faFileSignature as faFileSignatureDuo,
  faSparkles as   faSparklesDuo,
  faHouse as faHouseDuo,
  faGear as faGearDuo,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

/**
 * Module category classification
 * - core: Main navigation modules (always visible in nav if permitted)
 * - specialized: Apps shown in the Apps section
 * - utility: Helper modules that may not appear in navigation
 */
export type ModuleCategory = "core" | "specialized" | "utility";

/**
 * Color theme configuration for OS mode windows and dock icons
 */
export interface OsColorTheme {
  /** Theme type */
  type: "gradient" | "solid" | "glass" | "bordered" | "radial" | "conic" | "mesh";
  /** Tailwind gradient/background classes */
  colors: string;
  /** Icon color for gradient mode */
  iconColor: string;
  /** Icon color for colored mode (dark bg) */
  coloredIconColor?: string;
  /** Shadow classes */
  shadow?: string;
  /** Icon style - 'light' uses white icons, 'colored' uses colored icons */
  iconStyle?: "light" | "colored";
}

/**
 * Definition for a single module in the application
 */
export interface ModuleDefinition {
  /** Module ID in underscore format (e.g., "report_builder") */
  id: string;
  /** Route path in hyphen format (e.g., "/report-builder") */
  routePath: string;
  /** Display name for the module */
  label: string;
  /** Description for search and tooltips */
  description: string;
  /** FontAwesome icon for classic mode (regular variant) */
  icon: IconDefinition;
  /** Category classification */
  category: ModuleCategory;
  /** OS app ID if different from route segment (e.g., "workers" for worker module) */
  osAppId?: string;
  /** Whether to show in the dock (OS mode) */
  showInDock?: boolean;
  /** Whether to show in main navigation (classic mode) */
  showInNavigation?: boolean;
  /** Whether to show in Apps section */
  showInAppsSection?: boolean;
  /** FontAwesome icon for OS mode dock (duotone variant) */
  osIcon?: IconDefinition;
  /** Color theme for OS mode windows and dock icons */
  colorTheme?: OsColorTheme;
  /** Default window size for OS mode */
  defaultSize?: { width: number; height: number };
  /** Minimum window size for OS mode */
  minSize?: { width: number; height: number };
}

/**
 * Central registry of all application modules
 */
export const MODULE_REGISTRY: Record<string, ModuleDefinition> = {
  // ============================================================================
  // Core Navigation Modules
  // ============================================================================
  dashboard: {
    id: "dashboard",
    routePath: "/dashboard",
    label: "Dashboard",
    description: "View your main dashboard",
    icon: faTableColumns,
    osIcon: faObjectsColumnDuo,
    category: "core",
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "gradient",
      colors: "from-violet-600 via-purple-600 to-fuchsia-700",
      iconColor: "text-white",
      coloredIconColor: "text-violet-400",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 800 },
  },
  screener: {
    id: "screener",
    routePath: "/screener",
    label: "Screener",
    description: "Search and filter assets",
    icon: faFilters,
    osIcon: faFiltersDuo,
    category: "core",
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "solid",
      colors: "bg-slate-700 dark:bg-slate-800",
      iconColor: "text-violet-400",
      shadow: "shadow-lg",
      iconStyle: "colored",
    },
    defaultSize: { width: 1120, height: 768 },
  },
  playground: {
    id: "playground",
    routePath: "/playground",
    label: "Playground",
    description: "Interactive data analysis",
    icon: faChartMixed,
    osIcon: faChartMixedDuo,
    category: "core",
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "gradient",
      colors: "from-cyan-600 via-blue-600 to-blue-700",
      coloredIconColor: "text-cyan-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1400, height: 900 },
  },
  documents: {
    id: "documents",
    routePath: "/documents",
    label: "Documents",
    description: "Manage your documents",
    icon: faFolder,
    osIcon: faFolderDuo,
    category: "core",
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "solid",
      colors: "bg-slate-700 dark:bg-slate-800",
      coloredIconColor: "text-cyan-300",
      iconColor: "text-cyan-400",
      shadow: "shadow-lg",
      iconStyle: "colored",
    },
  },
  ops: {
    id: "ops",
    routePath: "/ops",
    label: "Operations",
    description: "System operations and monitoring",
    icon: faServer,
    osIcon: faServerDuo,
    category: "core",
    showInNavigation: true,
    colorTheme: {
      type: "solid",
      colors: "bg-slate-700 dark:bg-slate-800",
      iconColor: "text-slate-400",
      shadow: "shadow-lg",
      iconStyle: "colored",
    },
  },

  // ============================================================================
  // Reports & Report Builder
  // ============================================================================
  reports: {
    id: "reports",
    routePath: "/reports",
    label: "Reports",
    description: "View and manage reports",
    icon: faFileLines,
    osIcon: faFilesDuo,
    category: "core",
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-indigo-600 to-indigo-700",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-400",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1120, height: 768 },
  },
  report_builder: {
    id: "report_builder",
    routePath: "/report-builder",
    label: "Report Builder",
    description: "Create and edit custom reports",
    icon: faFilePen,
    osIcon: faPaintbrushPencilDuo,
    category: "core",
    osAppId: "report-builder",
    showInDock: true,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-indigo-600 to-indigo-700",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-400",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1400, height: 900 },
    minSize: { width: 1000, height: 600 },
  },

  // ============================================================================
  // Workers & Pods
  // ============================================================================
  worker: {
    id: "worker",
    routePath: "/worker",
    label: "Workers",
    description: "Manage AI workers and tasks",
    icon: faUserRobot,
    osIcon: faCommentsDuo,
    category: "core",
    osAppId: "workers",
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "gradient",
      colors: "from-emerald-600 via-emerald-600 to-teal-700",
      iconColor: "text-white",
      coloredIconColor: "text-emerald-400",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 700 },
  },
  missions: {
    id: "missions",
    routePath: "/missions",
    label: "Missions",
    description: "Active client missions and their tasks",
    icon: faBullseyeArrow,
    osIcon: faBullseyeArrowDuo,
    category: "core",
    osAppId: "missions",
    showInDock: true,
    showInNavigation: true,
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-violet-600 to-purple-700",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-400",
      shadow: "shadow-lg",
    },
  },
  clients: {
    id: "clients",
    routePath: "/clients",
    label: "Clients",
    description: "Household knowledge centre — missions, tasks, memories, timeline",
    icon: faAddressCard,
    osIcon: faAddressCardDuo,
    category: "core",
    osAppId: "clients",
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "gradient",
      colors: "from-sky-600 via-cyan-600 to-teal-700",
      iconColor: "text-white",
      coloredIconColor: "text-sky-400",
      shadow: "shadow-lg",
    },
  },
  leads: {
    id: "leads",
    routePath: "/leads",
    label: "Leads",
    description: "All leads — filterable list with lead records",
    icon: faUserPlus,
    osIcon: faUserPlusDuo,
    category: "core",
    osAppId: "leads",
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "gradient",
      colors: "from-teal-600 via-emerald-600 to-green-700",
      iconColor: "text-white",
      coloredIconColor: "text-teal-400",
      shadow: "shadow-lg",
    },
  },
  mission_control: {
    id: "mission_control",
    routePath: "/mission-control",
    label: "Mission Control",
    description: "Central hub for missions, tasks, and pipelines across the firm",
    icon: faRocket,
    osIcon: faRocketDuo,
    category: "core",
    osAppId: "mission-control",
    showInDock: true,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-purple-600 to-violet-700",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-400",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1100, height: 750 },
  },
  artifacts: {
    id: "artifacts",
    routePath: "/artifacts",
    label: "Artefacts",
    description: "Agentic v2 documents — letters, forms, templates",
    icon: faFiles,
    osIcon: faFilesDuo,
    category: "core",
    osAppId: "artifacts",
    showInDock: true,
    colorTheme: {
      type: "gradient",
      colors: "from-cyan-600 via-sky-600 to-blue-700",
      iconColor: "text-white",
      coloredIconColor: "text-cyan-400",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1100, height: 750 },
  },
  pod: {
    id: "pod",
    routePath: "/pod",
    label: "Pods",
    description: "Manage pods and collections",
    icon: faDiagramSubtask,
    osIcon: faDiagramSubtaskDuo,
    category: "core",
    osAppId: "pods",
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "gradient",
      colors: "from-orange-600 via-red-600 to-red-700",
      iconColor: "text-white",
      coloredIconColor: "text-orange-400",
      shadow: "shadow-lg",
    },
  },

  // ============================================================================
  // Portfolio & Product
  // ============================================================================
  // Permission gate for portfolio detail windows (screener, etc.) — not an OS launcher.
  portfolio: {
    id: "portfolio",
    routePath: "/portfolio",
    label: "Portfolio",
    description: "View and manage portfolios",
    icon: faBriefcase,
    osIcon: faBriefcaseDuo,
    category: "core",
    colorTheme: {
      type: "gradient",
      colors: "from-purple-600 via-purple-600 to-indigo-700",
      coloredIconColor: "text-purple-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1000, height: 700 },
  },
  // Permission gate for product detail windows — not an OS launcher.
  product: {
    id: "product",
    routePath: "/product",
    label: "Product",
    description: "View product details",
    icon: faBoxOpen,
    osIcon: faChartMixedDuo,
    category: "core",
    colorTheme: {
      type: "gradient",
      colors: "from-amber-600 via-orange-600 to-orange-700",
      coloredIconColor: "text-amber-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1000, height: 700 },
  },

  // ============================================================================
  // Specialized Apps
  // ============================================================================
  scenario_tracker: {
    id: "scenario_tracker",
    routePath: "/scenario-tracker",
    label: "Scenario Tracker",
    description: "Track and analyze scenarios",
    icon: faListCheck,
    osIcon: faListCheckDuo,
    category: "specialized",
    osAppId: "scenario-tracker",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-green-600 via-emerald-600 to-emerald-700",
      coloredIconColor: "text-green-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
  },
  meetings: {
    id: "meetings",
    routePath: "/meetings",
    label: "Meetings",
    description: "Client meetings, captures, transcripts, and extractions",
    icon: faCalendarCheck,
    osIcon: faCalendarCheckDuo,
    category: "specialized",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-violet-600 via-violet-600 to-fuchsia-700",
      iconColor: "text-white",
      coloredIconColor: "text-violet-300",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1120, height: 768 },
  },
  what_if: {
    id: "what_if",
    routePath: "/what-if",
    label: "What If",
    description: "What-if scenario analysis",
    icon: faQuestionCircle,
    osIcon: faQuestionCircleDuo,
    category: "specialized",
    osAppId: "what-if",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-yellow-600 via-amber-600 to-amber-700",
      coloredIconColor: "text-yellow-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
  },
  cash_flow: {
    id: "cash_flow",
    routePath: "/cash-flow",
    label: "Cash Flow",
    description: "Cash flow analysis and tracking",
    icon: faSplit,
    osIcon: faSplitDuo,
    category: "specialized",
    osAppId: "cash-flow",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-lime-600 via-green-600 to-green-700",
      coloredIconColor: "text-lime-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1167, height: 800 },
  },
  add_products: {
    id: "add_products",
    routePath: "/add-products",
    label: "Add Products",
    description: "Search for or create products",
    icon: faCirclePlus,
    osIcon: faCirclePlusDuo,
    category: "specialized",
    osAppId: "add-products",
    showInAppsSection: true,
    showInDock: true,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-indigo-600 to-violet-700",
      coloredIconColor: "text-indigo-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1167, height: 800 },
  },
  fund_processor: {
    id: "fund_processor",
    routePath: "/fund-processor",
    label: "Fund Processor",
    description: "Process and permission funds",
    icon: faChartPie,
    osIcon: faChartPieDuo,
    category: "specialized",
    osAppId: "fund-processor",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-sky-600 via-blue-600 to-indigo-700",
      coloredIconColor: "text-sky-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1167, height: 800 },
  },
  equity_processor: {
    id: "equity_processor",
    routePath: "/equity-processor",
    label: "Equity Processor",
    description: "Process and permission equities",
    icon: faChartLine,
    osIcon: faChartLineDuo,
    category: "specialized",
    osAppId: "equity-processor",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-emerald-600 via-green-600 to-teal-700",
      coloredIconColor: "text-emerald-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1167, height: 800 },
  },
  bond_processor: {
    id: "bond_processor",
    routePath: "/bond-processor",
    label: "Bond Processor",
    description: "Process and permission bonds",
    icon: faBalanceScale,
    osIcon: faBalanceScaleDuo,
    category: "specialized",
    osAppId: "bond-processor",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-amber-600 via-orange-600 to-orange-700",
      coloredIconColor: "text-amber-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1167, height: 800 },
  },
  custom_products: {
    id: "custom_products",
    routePath: "/custom-products",
    label: "Custom Products",
    description: "Create custom products to track your own performance data",
    icon: faBoxOpen,
    osIcon: faBoxOpen,
    category: "specialized",
    osAppId: "custom-products",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-cyan-600 via-teal-600 to-teal-700",
      coloredIconColor: "text-cyan-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1167, height: 800 },
  },
  trade_blotter: {
    id: "trade_blotter",
    routePath: "/trade-blotter",
    label: "Trade Blotter",
    description: "Track and execute trades",
    icon: faTable,
    osIcon: faTableDuo,
    category: "specialized",
    osAppId: "trade-blotter",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-red-600 via-orange-600 to-orange-700",
      coloredIconColor: "text-red-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
  },
  portfolio_builder: {
    id: "portfolio_builder",
    routePath: "/portfolio-builder",
    label: "Portfolio Builder",
    description: "Build and manage portfolios",
    icon: faBriefcase,
    osIcon: faBriefcaseDuo,
    category: "specialized",
    osAppId: "portfolio-builder",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-purple-700 via-purple-700 to-indigo-800",
      coloredIconColor: "text-purple-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
  },
  data_ingest: {
    id: "data_ingest",
    routePath: "/data-hub",
    label: "Data Hub",
    description: "Data ingestion and management",
    icon: faWrench,
    osIcon: faWrenchDuo,
    category: "specialized",
    osAppId: "data-hub",
    showInAppsSection: true,
    colorTheme: {
      type: "solid",
      colors: "bg-slate-700 dark:bg-slate-800",
      coloredIconColor: "text-slate-300",
      iconColor: "text-slate-300",
      shadow: "shadow-lg",
      iconStyle: "colored",
    },
  },
  benchmark_builder: {
    id: "benchmark_builder",
    routePath: "/benchmark-builder",
    label: "Benchmark Builder",
    description: "Create composite benchmarks with weighted combinations",
    icon: faCalculator,
    osIcon: faCalculatorDuo,
    category: "specialized",
    osAppId: "benchmark-builder",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-orange-700 via-red-700 to-red-800",
      coloredIconColor: "text-orange-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
  },
  monte_carlo: {
    id: "monte_carlo",
    routePath: "/monte-carlo",
    label: "Monte Carlo",
    description: "Stochastic portfolio forecasting and simulation",
    icon: faDice,
    osIcon: faDiceDuo,
    category: "specialized",
    osAppId: "monte-carlo",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-purple-600 via-purple-700 to-purple-800",
      coloredIconColor: "text-purple-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 800 },
  },
  optimiser: {
    id: "optimiser",
    routePath: "/optimiser",
    label: "Optimiser",
    description: "Portfolio optimisation and efficient frontier analysis",
    icon: faSliders,
    osIcon: faSlidersDuo,
    category: "specialized",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-emerald-600 via-emerald-700 to-emerald-800",
      coloredIconColor: "text-emerald-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 800 },
  },
  factor_analysis: {
    id: "factor_analysis",
    routePath: "/factor-analysis",
    label: "Factor Analysis",
    description: "Regression-based factor exposure analysis",
    icon: faChartLine,
    osIcon: faChartLineDuo,
    category: "specialized",
    osAppId: "factor-analysis",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-sky-600 via-sky-700 to-sky-800",
      coloredIconColor: "text-sky-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 800 },
  },
  portfolio_lab: {
    id: "portfolio_lab",
    routePath: "/portfolio-lab",
    label: "Portfolio Lab",
    description: "Unified workspace for portfolio analysis",
    icon: faFlask,
    osIcon: faFlaskDuo,
    category: "specialized",
    osAppId: "portfolio-lab",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-violet-600 via-violet-700 to-violet-800",
      coloredIconColor: "text-violet-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1400, height: 900 },
  },
  cmas: {
    id: "cmas",
    routePath: "/cmas",
    label: "CMAs",
    description: "Capital Market Assumptions - expected returns, volatilities, and proxies",
    icon: faChartPie,
    osIcon: faChartPieDuo,
    category: "specialized",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-teal-600 via-teal-700 to-teal-800",
      coloredIconColor: "text-teal-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 800 },
  },
  advisor_dashboard: {
    id: "advisor_dashboard",
    routePath: "/advisor-dashboard",
    label: "Advisor Dashboard",
    description: "Advisor-specific dashboard and tools",
    icon: faGlobe,
    osIcon: faGlobeDuo,
    category: "specialized",
    osAppId: "advisor-dashboard",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-indigo-700 to-indigo-800",
      coloredIconColor: "text-indigo-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1400, height: 900 },
  },
  home: {
    id: "home",
    routePath: "/home",
    label: "Home",
    description: "Role-based workspace — paraplanner command centre, tasks, and coverage",
    icon: faHouse,
    osIcon: faHouseDuo,
    category: "core",
    osAppId: "home",
    showInAppsSection: true,
    showInDock: true,
    showInNavigation: true,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-violet-600 to-purple-700",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-300",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1280, height: 860 },
    minSize: { width: 900, height: 600 },
  },
  settings: {
    id: "settings",
    routePath: "/settings",
    label: "Settings",
    description: "Personal preferences and company branding",
    icon: faGear,
    osIcon: faGearDuo,
    category: "utility",
    osAppId: "settings",
    showInDock: false,
    showInNavigation: false,
    colorTheme: {
      type: "solid",
      colors: "bg-slate-700 dark:bg-slate-800",
      iconColor: "text-slate-300",
      coloredIconColor: "text-slate-400",
      shadow: "shadow-lg",
      iconStyle: "colored",
    },
    defaultSize: { width: 900, height: 700 },
    minSize: { width: 640, height: 480 },
  },
  case_checking: {
    id: "case_checking",
    routePath: "/case-checking",
    label: "Case Checking",
    description: "FCA compliance case assessment workflow",
    icon: faClipboardCheck,
    osIcon: faClipboardCheckDuo,
    category: "specialized",
    osAppId: "case-checking",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-teal-600 via-emerald-600 to-emerald-700",
      coloredIconColor: "text-teal-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 800 },
  },
  disclosures: {
    id: "disclosures",
    routePath: "/disclosures",
    label: "Disclosures",
    description: "Clara — large position regulatory disclosures workflow",
    icon: faFileSignature,
    osIcon: faFileSignatureDuo,
    category: "specialized",
    osAppId: "disclosures",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-sky-500 via-blue-500 to-cyan-500",
      coloredIconColor: "text-sky-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1280, height: 820 },
  },
  // ============================================================================
  // Additional Modules
  // ============================================================================
  rebalancing: {
    id: "rebalancing",
    routePath: "/rebalancing",
    label: "Rebalancing",
    description: "Portfolio rebalancing tools",
    icon: faBalanceScale,
    osIcon: faBalanceScaleDuo,
    category: "specialized",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-amber-600 via-amber-700 to-amber-800",
      coloredIconColor: "text-amber-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1400, height: 800 },
  },
  twr: {
    id: "twr",
    routePath: "/twr",
    label: "TWR Analysis",
    description: "Time-weighted return analysis",
    icon: faChartLine,
    osIcon: faChartLineDuo,
    category: "specialized",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-rose-600 via-rose-700 to-rose-800",
      coloredIconColor: "text-rose-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 700 },
  },
  irr: {
    id: "irr",
    routePath: "/irr",
    label: "IRR Analysis",
    description: "Internal rate of return analysis",
    icon: faCalculator,
    osIcon: faCalculatorDuo,
    category: "specialized",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-pink-600 via-pink-700 to-pink-800",
      coloredIconColor: "text-pink-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 700 },
  },
  calculators: {
    id: "calculators",
    routePath: "/calculators",
    label: "Calculators",
    description: "Client-scoped paraplanner tools — fees, plan features, and more",
    icon: faCalculator,
    osIcon: faCalculatorDuo,
    category: "specialized",
    osAppId: "calculators",
    showInAppsSection: true,
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-violet-600 to-purple-700",
      coloredIconColor: "text-indigo-400",
      iconColor: "text-white",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1000, height: 720 },
    minSize: { width: 720, height: 480 },
  },
  pulse_v2: {
    id: "pulse_v2",
    routePath: "/pulse-v2",
    label: "Pulse",
    description: "Intelligent Q&A over your data powered by Ontology Temporal RAG",
    icon: faSparkles,
    osIcon: faSparklesDuo,
    category: "specialized",
    osAppId: "pulse-v2",
    showInAppsSection: true,
    showInDock: true,
    colorTheme: {
      type: "gradient",
      colors: "from-violet-600 via-purple-600 to-fuchsia-700",
      iconColor: "text-white",
      coloredIconColor: "text-violet-400",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1200, height: 800 },
  },

  // ============================================================================
  // Utility Modules (may not appear in navigation)
  // ============================================================================
  ani: {
    id: "ani",
    routePath: "/ani",
    label: "ANI",
    description: "ANI assistant interface",
    icon: faUserRobot,
    category: "utility",
  },
};

/**
 * OS apps that surfaces can link from their app nav but that are not permission modules
 * (window access is gated by the OS app's own `module`).
 */
export const NAV_ONLY_APPS: Record<string, ModuleDefinition> = {
  fca_portal: {
    id: "fca_portal",
    routePath: "/fca-portal",
    label: "Applicant portal",
    description: "FCA demo — the applicant firm's view of its authorisation application",
    icon: faBriefcase,
    osIcon: faBriefcaseDuo,
    category: "utility",
    osAppId: "fca-portal",
    colorTheme: {
      type: "gradient",
      colors: "from-indigo-600 via-violet-600 to-purple-700",
      iconColor: "text-white",
      coloredIconColor: "text-indigo-400",
      shadow: "shadow-lg",
    },
    defaultSize: { width: 1080, height: 760 },
    minSize: { width: 560, height: 480 },
  },
};

// ============================================================================
// Permission-Only Modules (not in MODULE_REGISTRY)
// ============================================================================
// These modules are checked via hasPermission() but don't have routes/UI entries:
//
// - ani_os: Controls access to OS Layout mode (windowed interface).
//           Users without this permission are restricted to Classic Layout.
//           See docs/layout-toggle-feature.md for details.
//
// - worker_quick_actions: Controls visibility of "Recent Quick Actions" in Worker Directory.
//                         Users without this permission will not see the quick actions section.
//

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get all module IDs
 */
export function getAllModuleIds(): string[] {
  return Object.keys(MODULE_REGISTRY);
}

/**
 * Get a module definition by ID
 * Handles both underscore and hyphen formats
 */
export function getModuleById(id: string): ModuleDefinition | undefined {
  const normalizedId = id.replace(/-/g, "_");
  return MODULE_REGISTRY[normalizedId];
}

/**
 * Get a map of route paths (without leading slash) to module IDs
 * Compatible with existing routeModules format in base.tsx
 */
export function getRouteToModuleMap(): Record<string, string> {
  const map: Record<string, string> = {};
  Object.values(MODULE_REGISTRY).forEach((module) => {
    const routeKey = module.routePath.replace(/^\//, "");
    map[routeKey] = module.id;
  });
  return map;
}

/**
 * Get modules filtered by category
 */
export function getModulesByCategory(category: ModuleCategory): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY).filter((module) => module.category === category);
}

/**
 * Check if a module definition exists
 * Handles both underscore and hyphen formats
 */
export function hasModuleDefinition(moduleId: string): boolean {
  if (!moduleId) return false;
  const normalizedId = moduleId.replace(/-/g, "_");
  return normalizedId in MODULE_REGISTRY;
}

/**
 * Get all specialized module IDs (for Apps section visibility checks)
 */
export function getSpecializedModuleIds(): string[] {
  return Object.values(MODULE_REGISTRY)
    .filter((module) => module.category === "specialized")
    .map((module) => module.id);
}

/**
 * Get modules that should show in the Apps section
 */
export function getAppsModules(): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY).filter((module) => module.showInAppsSection === true);
}

/**
 * Get modules that should show in the main navigation
 */
export function getNavigationModules(): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY).filter((module) => module.showInNavigation === true);
}

/**
 * Get modules that should show in the dock
 */
export function getDockModules(): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY).filter((module) => module.showInDock === true);
}

/**
 * Determines if the Apps section should be shown based on permissions.
 * The Apps section should be visible when:
 * 1. Not in single_app mode
 * 2. User has permission for at least one module in {@link getAppsModules}
 *
 * @param singleApp - Whether the user is in single app mode
 * @param hasPermission - Function to check module permissions
 * @returns True if the Apps section should be shown
 */
export function shouldShowAppsSection(
  singleApp: boolean,
  hasPermission: (module: string) => boolean
): boolean {
  if (singleApp) {
    return false;
  }

  // Align with classic nav Apps list (getSpecializedAppsByPermission / getAppsModules)
  return getAppsModules().some((module) => hasPermission(module.id));
}
